import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setSelectedRestaurantId } from '../../redux/slices/restaurantSlice';
import { Toggle } from '../common/Toggle';
import { Dropdown } from '../common/Dropdown';

// Static dropdown options
const VOICE_MODEL_OPTIONS = [
  { value: 'elliot', label: 'Elliot (Male)' },
  { value: 'tara', label: 'Tara (Female)' },
  { value: 'sagar', label: 'Sagar (Male - Indian)' },
  { value: 'clara', label: 'Clara (Female)' },
];

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
];

const PREP_DURATION_OPTIONS = [
  { value: 'fifteen minutes', label: '15 minutes' },
  { value: 'twenty minutes', label: '20 minutes' },
  { value: 'twenty-five minutes', label: '25 minutes' },
  { value: 'thirty minutes', label: '30 minutes' },
  { value: 'thirty-five minutes', label: '35 minutes' },
  { value: 'forty minutes', label: '40 minutes' },
  { value: 'forty-five minutes', label: '45 minutes' },
  { value: 'fifty minutes', label: '50 minutes' },
  { value: 'one hour', label: '1 hour' },
];

interface VoiceConfigTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  endpoint: {
    title: string;
    method: string;
    endpoint: string;
    parameters?: any;
  };
}

export const VoiceConfigTestModal: React.FC<VoiceConfigTestModalProps> = ({ 
  isOpen, 
  onClose, 
  endpoint 
}) => {
  const dispatch = useAppDispatch();
  const { apiBaseUrl, currentApiKey, environment } = useAppSelector((state) => state.auth);
  const { restaurants, selectedRestaurantId: reduxSelectedRestaurantId } = useAppSelector((state) => state.restaurant);
  
  // Auth state
  const [restaurantId, setRestaurantId] = useState('');
  const [selectedRestaurantName, setSelectedRestaurantName] = useState('');
  
  // Mode state
  const [mode, setMode] = useState<'CREATE' | 'UPDATE'>('CREATE');
  
  // Voice config state
  const [configId, setConfigId] = useState('');
  const [assignedAgentNumber, setAssignedAgentNumber] = useState('');
  
  // Original values for change detection
  const [newCustomerGreeting, setNewCustomerGreeting] = useState('');
  const [existingCustomerGreeting, setExistingCustomerGreeting] = useState('');
  const [endTemplate, setEndTemplate] = useState('');
  const [agentName, setAgentName] = useState('');
  const [voiceModel, setVoiceModel] = useState('elliot');
  const [primaryLanguage, setPrimaryLanguage] = useState('en');
  const [preparationDuration, setPreparationDuration] = useState('twenty-five minutes');
  
  // Toggle states
  const [canTransferCall, setCanTransferCall] = useState(false);
  const [callTransferNumber, setCallTransferNumber] = useState('');
  const [canSendPaymentLink, setCanSendPaymentLink] = useState(false);
  const [canSendOrderConfirmationSms, setCanSendOrderConfirmationSms] = useState(false);
  const [canTakeOffOrders, setCanTakeOffOrders] = useState(false);
  const [canTakeDelivery, setCanTakeDelivery] = useState(false);
  const [transferDeliveryOrders, setTransferDeliveryOrders] = useState(false);
  
  // Response state
  const [response, setResponse] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [authExpanded, setAuthExpanded] = useState(true);

  // Refs for textareas to handle placeholder insertion
  const newCustomerGreetingRef = useRef<HTMLTextAreaElement>(null);
  const existingCustomerGreetingRef = useRef<HTMLTextAreaElement>(null);
  const endTemplateRef = useRef<HTMLTextAreaElement>(null);

  // Placeholder insertion function
  const insertPlaceholder = (placeholder: string, field: 'new' | 'existing' | 'end') => {
    if (!placeholder) return;
    
    let ref, value, setValue;
    
    if (field === 'new') {
      ref = newCustomerGreetingRef;
      value = newCustomerGreeting;
      setValue = setNewCustomerGreeting;
    } else if (field === 'existing') {
      ref = existingCustomerGreetingRef;
      value = existingCustomerGreeting;
      setValue = setExistingCustomerGreeting;
    } else {
      ref = endTemplateRef;
      value = endTemplate;
      setValue = setEndTemplate;
    }
    
    const textarea = ref.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = value.substring(0, start) + placeholder + value.substring(end);
    
    setValue(newValue);
    
    // Set cursor position after inserted placeholder
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 0);
  };

  // Initialize restaurant selection from Redux
  useEffect(() => {
    if (isOpen && reduxSelectedRestaurantId && restaurants.length > 0) {
      const restaurant = restaurants.find(r => r.restaurantId === reduxSelectedRestaurantId);
      if (restaurant) {
        setRestaurantId(restaurant.restaurantId);
        setSelectedRestaurantName(restaurant.name);
      }
    }
  }, [isOpen, reduxSelectedRestaurantId, restaurants]);

  // Auto-fetch config when restaurant is selected (always for Voice Configuration)
  useEffect(() => {
    const fetchConfig = async () => {
      if (restaurantId && (endpoint.method === 'POST' || endpoint.method === 'PUT')) {
        try {
          const response = await fetch(
            `${apiBaseUrl}${endpoint.endpoint}`,
            {
              method: 'GET',
              headers: {
                'api-key': currentApiKey || '',
                'restaurant-id': restaurantId,
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            // Handle both array response and object with data property
            const config = Array.isArray(data) ? data[0] : (data.data?.[0] || data[0]);
            
            if (config && config.id) {
              // UPDATE MODE - Config exists
              setMode('UPDATE');
              setConfigId(config.id?.toString() || '');
              // Support both snake_case (API) and camelCase (future)
              setNewCustomerGreeting(config.newCustomerGreeting || config.new_customer_greeting || '');
              setExistingCustomerGreeting(config.existingCustomerGreeting || config.existing_customer_greeting || '');
              setEndTemplate(config.endTemplate || config.end_template || '');
              setAgentName(config.agentName || config.agent_name || '');
              setVoiceModel(config.voiceModel || config.voice_model || 'elliot');
              setPrimaryLanguage(config.primaryLanguage || config.primary_language || 'en');
              setPreparationDuration(config.preparationDuration || config.preparation_duration || 'twenty-five minutes');
              setCanTransferCall((config.canTransferCall || config.can_transfer_call) === 'true');
              setCallTransferNumber(config.callTransferNumber || config.call_transfer_number || '');
              setCanSendPaymentLink((config.canSendPaymentLink || config.can_send_payment_link) === 'true');
              setCanSendOrderConfirmationSms((config.canSendOrderConfirmationSms || config.can_send_order_confirmation_sms) === 'true');
              setCanTakeOffOrders((config.canTakeOffOrders || config.can_take_off_orders) === 'true');
              setCanTakeDelivery((config.canTakeDelivery || config.can_take_delivery) === 'true' || config.canTakeDelivery === '1');
              setTransferDeliveryOrders((config.transferDeliveryOrders || config.transfer_delivery_orders) === 'true');
              // Extract and store assigned agent number
              setAssignedAgentNumber(config.agentNumber || config.agent_number || '');
            } else {
              // CREATE MODE - No config exists
              setMode('CREATE');
              setConfigId('');
              setAssignedAgentNumber('');  // Clear assigned number
              // Clear all fields
              setNewCustomerGreeting('');
              setExistingCustomerGreeting('');
              setEndTemplate('');
              setAgentName('');
              setVoiceModel('elliot');
              setPrimaryLanguage('en');
              setPreparationDuration('twenty-five minutes');
              setCanTransferCall(false);
              setCallTransferNumber('');
              setCanSendPaymentLink(false);
              setCanSendOrderConfirmationSms(false);
              setCanTakeOffOrders(false);
              setCanTakeDelivery(false);
              setTransferDeliveryOrders(false);
            }
          }
        } catch (error) {
          console.error('Error fetching config:', error);
          // On error, default to CREATE mode
          setMode('CREATE');
        }
      }
    };

    fetchConfig();
  }, [restaurantId, apiBaseUrl, endpoint.endpoint, endpoint.method, currentApiKey]);

  const getMethodBadgeStyles = (method: string) => {
    const baseStyles = 'inline-flex items-center px-3 py-1 rounded text-sm font-semibold uppercase tracking-wide';
    
    switch (method.toUpperCase()) {
      case 'GET':
        return `${baseStyles} bg-green-100 text-green-800`;
      case 'POST':
        return `${baseStyles} bg-blue-100 text-blue-800`;
      case 'PUT':
        return `${baseStyles} bg-orange-100 text-orange-800`;
      default:
        return `${baseStyles} bg-gray-100 text-gray-800`;
    }
  };

  const generateCurlCommand = () => {
    const headers = [
      `'api-key: ${currentApiKey || '<api-key>'}'`,
      `'restaurant-id: ${restaurantId || '<restaurant-id>'}'`
    ];
    
    // Use the actual endpoint method (GET, POST, PUT, etc.)
    const actualMethod = endpoint.method;
    
    let bodyData = '';
    if (endpoint.method === 'PUT' || endpoint.method === 'POST') {
      const body = mode === 'UPDATE' ? {
        id: parseInt(configId) || 1,
        newCustomerGreeting,
        existingCustomerGreeting,
        endTemplate,
        agentName,
        voiceModel,
        primaryLanguage,
        canTransferCall,
        // canSendPaymentLink,  // COMMENTED OUT
        canSendOrderConfirmationSms,
        canTakeOffOrders,
        canTakeDelivery,
        transferDeliveryOrders,
        preparationDuration,
      } : {
        newCustomerGreeting,
        existingCustomerGreeting,
        endTemplate,
        agentName,
        voiceModel,
        primaryLanguage,
        canTransferCall,
        callTransferNumber,
        canSendPaymentLink,
        canSendOrderConfirmationSms,
        canTakeOffOrders,
        canTakeDelivery,
        transferDeliveryOrders,
        preparationDuration,
      };
      
      bodyData = ` \\\n  --header 'Content-Type: application/json' \\\n  --data '${JSON.stringify(body, null, 2)}'`;
    }

    return `curl --request ${actualMethod} \\\n  --url ${apiBaseUrl}${endpoint.endpoint} \\\n  --header ${headers.join(' \\\n  --header ')}${bodyData}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleTest = async () => {
    setIsLoading(true);
    setResponse('');

    try {
      const headers: Record<string, string> = {
        'api-key': currentApiKey || '',
        'restaurant-id': restaurantId,
      };

      // Use the actual endpoint method (GET, POST, PUT, etc.)
      const actualMethod = endpoint.method;

      const fetchOptions: RequestInit = {
        method: actualMethod,
        headers,
      };

      if (endpoint.method === 'PUT' || endpoint.method === 'POST') {
        headers['Content-Type'] = 'application/json';
        
        const body = mode === 'UPDATE' ? {
          id: parseInt(configId) || 1,
          newCustomerGreeting,
          existingCustomerGreeting,
          endTemplate,
          agentName,
          voiceModel,
          primaryLanguage,
          canTransferCall,
          callTransferNumber,
          // canSendPaymentLink,  // COMMENTED OUT
          canSendOrderConfirmationSms,
          canTakeOffOrders,
          canTakeDelivery,
          transferDeliveryOrders,
          preparationDuration,
        } : {
          newCustomerGreeting,
          existingCustomerGreeting,
          endTemplate,
          agentName,
          voiceModel,
          primaryLanguage,
          canTransferCall,
          callTransferNumber,
          canSendPaymentLink,
          canSendOrderConfirmationSms,
          canTakeOffOrders,
          canTakeDelivery,
          transferDeliveryOrders,
          preparationDuration,
        };
        
        fetchOptions.body = JSON.stringify(body);
      }

      const response = await fetch(
        `${apiBaseUrl}${endpoint.endpoint}`,
        fetchOptions
      );

      // Try to parse as JSON, fall back to text if it fails
      try {
        const responseData = await response.json();
        setResponse(JSON.stringify(responseData, null, 2));
      } catch (jsonError) {
        // If JSON parsing fails, show the raw text response
        const text = await response.text();
        setResponse(text || 'Empty response');
      }
    } catch (error) {
      setResponse(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 backdrop-blur-sm bg-white/30 flex items-center justify-center p-4">
      <div className="bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)] max-w-7xl w-full min-w-[1200px] min-h-[600px] max-h-[95vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <div className="flex items-center space-x-3">
            <h3 className="text-base font-semibold text-[var(--text-primary)]">
              {endpoint.title}
            </h3>
          </div>
          
          {/* Right Side - Method, Endpoint and Send Button */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <span className={`text-xs px-2 py-1 rounded font-semibold ${
                environment === 'sandbox' 
                  ? 'bg-amber-100 text-amber-800' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {environment === 'sandbox' ? 'Sandbox' : 'Production'}
              </span>
              <span className={getMethodBadgeStyles(endpoint.method)}>
                {endpoint.method}
              </span>
              <code className="text-[var(--text-primary)] font-mono text-sm">
                {endpoint.endpoint}
              </code>
              <button
                onClick={() => copyToClipboard(`${apiBaseUrl}${endpoint.endpoint}`)}
                className="text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors p-1"
                title="Copy URL"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
            
            <button
              onClick={handleTest}
              disabled={isLoading}
              className="px-4 py-2 bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <span>{isLoading ? 'Sending...' : 'Send'}</span>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
              </svg>
            </button>
            
            <button 
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="flex flex-1 overflow-hidden min-w-full min-h-[500px]">
          {/* Left Side - Configuration Form */}
          <div className="flex-1 min-w-[600px] min-h-full p-6 overflow-y-auto border-r border-[var(--border-color)]">
            <div className="space-y-6">
              {/* Voice Order Number Display */}
              {assignedAgentNumber && (
                <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                  <p className="text-sm font-semibold text-blue-900 mb-1">Voice Order Number</p>
                  <p className="text-base font-mono text-blue-900">+1 {assignedAgentNumber}</p>
                  <p className="text-xs text-blue-700 mt-1">This number is assigned to handle customer voice orders.</p>
                </div>
              )}
              
              <p className="text-[var(--text-secondary)]">
                Configure voice settings for your restaurant's AI voice agent
              </p>

              {/* Authorization Section */}
              <div className="border border-[var(--border-color)] rounded-lg">
                <button
                  onClick={() => setAuthExpanded(!authExpanded)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-[var(--bg-hover)] transition-colors"
                >
                  <h4 className="text-base font-semibold text-[var(--text-primary)]">Authorization</h4>
                  <svg 
                    className={`w-4 h-4 transition-transform ${authExpanded ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {authExpanded && (
                  <div className="px-4 pb-4 border-t border-[var(--border-color)]">
                    <div className="mt-4 space-y-4">
                      {/* API Key Display */}
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <h5 className="text-sm font-medium text-[var(--accent-color)]">API-Key</h5>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">string</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">required</span>
                        </div>
                        <p className="text-[var(--text-secondary)] text-sm mb-3">API key</p>
                        <div className="w-full px-3 py-2 text-sm bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] font-mono">
                          {currentApiKey || 'Not available - please log in'}
                        </div>
                      </div>

                      {/* Restaurant Selection */}
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <h5 className="text-sm font-medium text-[var(--accent-color)]">restaurant</h5>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">select</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">required</span>
                        </div>
                        <p className="text-[var(--text-secondary)] text-sm mb-3">Select restaurant from your account</p>
                        {restaurants.length > 0 ? (
                          <select
                            value={selectedRestaurantName}
                            onChange={(e) => {
                              const selectedName = e.target.value;
                              setSelectedRestaurantName(selectedName);
                              const restaurant = restaurants.find(r => r.name === selectedName);
                              if (restaurant) {
                                setRestaurantId(restaurant.restaurantId);
                                dispatch(setSelectedRestaurantId(restaurant.restaurantId));
                              }
                            }}
                            className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                          >
                            <option value="">Select a restaurant</option>
                            {restaurants.map((restaurant) => (
                              <option key={restaurant.restaurantId} value={restaurant.name}>
                                {restaurant.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="text-[var(--text-secondary)] text-sm p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)]">
                            No restaurants available. Please register a restaurant first.
                          </div>
                        )}
                        {restaurantId && (
                          <p className="text-[var(--text-muted)] text-xs mt-2">
                            ID: {restaurantId}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Voice Configuration Fields - Only show when restaurant is selected */}
              {restaurantId && (endpoint.method === 'PUT' || endpoint.method === 'POST') && (
                <div className="space-y-6">
                  <h4 className="text-base font-semibold text-[var(--text-primary)]">body parameter</h4>
                  
                  {/* Customer Greetings Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0 }}
                    className="border border-[var(--border-color)] rounded-lg p-4 space-y-4"
                  >
                    <h5 className="text-sm font-semibold text-[var(--text-primary)]">
                      Customer Greetings
                    </h5>
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                        New Customer Greeting
                      </label>
                      
                      {/* Placeholder Dropdown */}
                      <div className="mb-2">
                        <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                          Insert Placeholders
                        </label>
                        <select
                          onChange={(e) => {
                            insertPlaceholder(e.target.value, 'new');
                            e.target.value = '';
                          }}
                          className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--accent-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]"
                        >
                          <option value="">Insert Placeholder...</option>
                          <option value="${restaurantName}">Restaurant Name</option>
                          <option value="${agentName}">Agent Name</option>
                          <option value="${customerNumber}">Customer Number</option>
                          <option value="${customerName}">Customer Name</option>
                          <option value="${preparationDuration}">Preparation Duration</option>
                        </select>
                      </div>
                      
                      <textarea
                        ref={newCustomerGreetingRef}
                        value={newCustomerGreeting}
                        onChange={(e) => setNewCustomerGreeting(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                        placeholder="Greeting for new customers"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                        Existing Customer Greeting
                      </label>
                      
                      {/* Placeholder Dropdown */}
                      <div className="mb-2">
                        <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                          Insert Placeholders
                        </label>
                        <select
                          onChange={(e) => {
                            insertPlaceholder(e.target.value, 'existing');
                            e.target.value = '';
                          }}
                          className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--accent-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]"
                        >
                          <option value="">Insert Placeholder...</option>
                          <option value="${restaurantName}">Restaurant Name</option>
                          <option value="${agentName}">Agent Name</option>
                          <option value="${customerNumber}">Customer Number</option>
                          <option value="${customerName}">Customer Name</option>
                          <option value="${preparationDuration}">Preparation Duration</option>
                        </select>
                      </div>
                      
                      <textarea
                        ref={existingCustomerGreetingRef}
                        value={existingCustomerGreeting}
                        onChange={(e) => setExistingCustomerGreeting(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                        placeholder="Greeting for returning customers"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                        End Template
                      </label>
                      
                      {/* Placeholder Dropdown */}
                      <div className="mb-2">
                        <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                          Insert Placeholders
                        </label>
                        <select
                          onChange={(e) => {
                            insertPlaceholder(e.target.value, 'end');
                            e.target.value = '';
                          }}
                          className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--accent-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]"
                        >
                          <option value="">Insert Placeholder...</option>
                          <option value="${restaurantName}">Restaurant Name</option>
                          <option value="${agentName}">Agent Name</option>
                          <option value="${customerNumber}">Customer Number</option>
                          <option value="${customerName}">Customer Name</option>
                          <option value="${preparationDuration}">Preparation Duration</option>
                        </select>
                      </div>
                      
                      <textarea
                        ref={endTemplateRef}
                        value={endTemplate}
                        onChange={(e) => setEndTemplate(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                        placeholder="Message when ending the call"
                      />
                    </div>
                  </motion.div>

                  {/* Voice & Language Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    className="border border-[var(--border-color)] rounded-lg p-4 space-y-4"
                  >
                    <h5 className="text-sm font-semibold text-[var(--text-primary)]">
                      Voice & Language
                    </h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                          Agent Name
                        </label>
                        <input
                          type="text"
                          value={agentName}
                          onChange={(e) => setAgentName(e.target.value)}
                          className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                          placeholder="e.g., Alex"
                        />
                      </div>

                      <Dropdown
                        label="Voice Model"
                        value={voiceModel}
                        onChange={setVoiceModel}
                        options={VOICE_MODEL_OPTIONS}
                      />

                      <Dropdown
                        label="Language"
                        value={primaryLanguage}
                        onChange={setPrimaryLanguage}
                        options={LANGUAGE_OPTIONS}
                      />
                    </div>
                  </motion.div>

                  {/* Call Management Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                    className="border border-[var(--border-color)] rounded-lg p-4 space-y-4"
                  >
                    <h5 className="text-sm font-semibold text-[var(--text-primary)]">
                      Call Management
                    </h5>
                    
                    <div className="grid grid-cols-2 gap-3 items-start">
                      {/* Call Transfer Toggle with Phone Number */}
                      <div className="border border-[var(--border-color)] rounded-lg p-3">
                        <Toggle
                          label="Call Transfer"
                          description="Transfer calls to staff"
                          checked={canTransferCall}
                          onChange={setCanTransferCall}
                          size="sm"
                        />
                        
                        {/* Phone Number Input - Shows when toggle is ON */}
                        {canTransferCall && (
                          <div className="mt-3 pt-3 border-t border-[var(--border-color)]">
                            <label className="block text-xs font-medium text-[var(--text-primary)] mb-2">
                              Transfer Phone Number
                            </label>
                            <input
                              type="tel"
                              value={callTransferNumber}
                              onChange={(e) => setCallTransferNumber(e.target.value)}
                              className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                              placeholder="+1234567890"
                            />
                          </div>
                        )}
                      </div>

                      {/* Payment Links Toggle - COMMENTED OUT */}
                      {/* <div className="border border-[var(--border-color)] rounded-lg p-3">
                        <Toggle
                          label="Payment Links"
                          description="Send payment links"
                          checked={canSendPaymentLink}
                          onChange={setCanSendPaymentLink}
                          size="sm"
                        />
                      </div> */}

                      {/* SMS Confirmations Toggle */}
                      <div className="border border-[var(--border-color)] rounded-lg p-3">
                        <Toggle
                          label="SMS Confirmations"
                          description="Send SMS confirmations"
                          checked={canSendOrderConfirmationSms}
                          onChange={setCanSendOrderConfirmationSms}
                          size="sm"
                        />
                      </div>

                    </div>
                  </motion.div>

                  {/* Order Management Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    className="border border-[var(--border-color)] rounded-lg p-4 space-y-4"
                  >
                    <h5 className="text-sm font-semibold text-[var(--text-primary)]">
                      Order Management
                    </h5>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Off-hours Orders Toggle */}
                      <div className="border border-[var(--border-color)] rounded-lg p-3">
                        <Toggle
                          label="Off-hours Orders"
                          description="Accept off-hours orders"
                          checked={canTakeOffOrders}
                          onChange={setCanTakeOffOrders}
                          size="sm"
                        />
                      </div>

                      {/* Delivery Orders Toggle */}
                      <div className="border border-[var(--border-color)] rounded-lg p-3">
                        <Toggle
                          label="Delivery Orders"
                          description="Accept delivery orders"
                          checked={canTakeDelivery}
                          onChange={setCanTakeDelivery}
                          size="sm"
                        />
                      </div>

                      {/* Transfer Delivery Orders Toggle */}
                      <div className="border border-[var(--border-color)] rounded-lg p-3">
                        <Toggle
                          label="Transfer Delivery Orders"
                          description="Transfer delivery orders to staff"
                          checked={transferDeliveryOrders}
                          onChange={setTransferDeliveryOrders}
                          size="sm"
                        />
                      </div>

                      <Dropdown
                        label="Preparation Duration"
                        value={preparationDuration}
                        onChange={setPreparationDuration}
                        options={PREP_DURATION_OPTIONS}
                      />
                    </div>
                  </motion.div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - cURL and Response */}
          <div className="w-96 min-w-[384px] min-h-full flex-shrink-0 flex flex-col">
            {/* Top Half - cURL */}
            <div className="flex-1 min-h-0 max-h-[500px] bg-[var(--bg-secondary)] p-4 border-b border-[var(--border-color)]">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-[var(--text-primary)]">cURL</h4>
                <button
                  onClick={() => copyToClipboard(generateCurlCommand())}
                  className="text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors p-1"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
              <div className="bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4 h-full overflow-auto">
                <pre className="text-sm text-[var(--text-primary)] font-mono whitespace-pre-wrap">
                  <code>{generateCurlCommand()}</code>
                </pre>
              </div>
            </div>

            {/* Bottom Half - Response */}
            <div className="flex-1 min-h-0 max-h-[400px] bg-[var(--bg-secondary)] p-4 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-[var(--text-primary)]">
                  {isLoading ? 'Loading Response...' : response ? '200' : 'Response'}
                </h4>
                {response && !isLoading && (
                  <button
                    onClick={() => copyToClipboard(response)}
                    className="text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors p-1"
                    title="Copy response"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex-1 bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4 overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="flex items-center space-x-2 text-[var(--text-secondary)]">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm">Loading response...</span>
                    </div>
                  </div>
                ) : response ? (
                  <pre className="text-sm text-[var(--text-primary)] font-mono whitespace-pre-wrap break-words">
                    <code>{response}</code>
                  </pre>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-[var(--text-muted)] text-sm">
                      Click Send to test the API endpoint
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
